import React, { useState } from "react";
import { ChevronDown, PhoneCall, MessageSquare } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  OTHER_DEPARTMENT_CALLS,
  PRIMARY_DEPARTMENT_CALL,
} from "@/constants/departmentCalls.js";

const GuestDepartmentCallPanel = ({ guestSessionId }) => {
  const [othersOpen, setOthersOpen] = useState(false);
  const [lastDepartment, setLastDepartment] = useState("");

  const startLiveCall = (call) => {
    if (!guestSessionId) {
      toast.error("Your guest session is not ready yet.");
      return;
    }

    const displayDepartment = call.shortLabel || call.department;
    setLastDepartment(displayDepartment);
    window.dispatchEvent(new CustomEvent("hotel:startGuestDepartmentCall", {
      detail: {
        department: call.department,
        departmentLabel: displayDepartment,
      },
    }));
  };

  return (
    <section className="rounded-3xl border border-white/70 bg-white/90 p-4 shadow-[0_22px_52px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:p-5">
      <div className="mb-3.5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-slate-900 sm:text-xl">Need help?</h2>
          <p className="text-xs text-slate-500 sm:text-sm">
            Live audio call with the hotel staff
          </p>
        </div>

        <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span>Audio Ready</span>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {/* Action 1: Call Reception */}
          {(() => {
            const Icon = PRIMARY_DEPARTMENT_CALL.icon || PhoneCall;

            return (
              <Button
                type="button"
                onClick={() => startLiveCall(PRIMARY_DEPARTMENT_CALL)}
                className="group flex min-h-[72px] w-full items-center justify-start gap-3 rounded-2xl border border-emerald-600/30 bg-gradient-to-r from-emerald-600 to-emerald-700 px-3.5 py-3 text-left text-white shadow-md shadow-emerald-600/20 transition-all duration-200 hover:brightness-105 active:scale-[0.98]"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-white ring-1 ring-white/30 backdrop-blur-md transition-transform group-hover:scale-105">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-sm font-bold leading-tight">Call Reception</span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-white/90">
                    Live audio line to front desk
                  </span>
                </div>
              </Button>
            );
          })()}

          {/* Action 2: Message Concierge */}
          <Button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("hotel:openGuestChat"))}
            className="group flex min-h-[72px] w-full items-center justify-start gap-3 rounded-2xl border border-slate-800 bg-slate-900 px-3.5 py-3 text-left text-white shadow-md shadow-slate-900/20 transition-all duration-200 hover:bg-slate-800 active:scale-[0.98]"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500/20 text-amber-400 ring-1 ring-amber-400/30 transition-transform group-hover:scale-105">
              <MessageSquare className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="block text-sm font-bold leading-tight">Message Concierge</span>
              <span className="mt-0.5 block text-[11px] leading-snug text-slate-300">
                Text chat for towels, dining & info
              </span>
            </div>
          </Button>
        </div>

        <Button
          type="button"
          onClick={() => setOthersOpen((current) => !current)}
          className="flex min-h-[48px] w-full items-center justify-between gap-3 rounded-2xl border border-slate-200/90 bg-white/90 px-4 py-3 text-left shadow-sm transition-all hover:bg-white active:scale-[0.99]"
          aria-expanded={othersOpen}
        >
          <div className="min-w-0 flex-1">
            <span className="block text-xs font-bold text-slate-800 sm:text-sm">Call other departments</span>
            <span className="block text-[11px] text-slate-500">Housekeeping, dining, security, maintenance</span>
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${othersOpen ? "rotate-180 text-slate-800" : ""}`}
          />
        </Button>

        <AnimatePresence initial={false}>
          {othersOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-2">
                {OTHER_DEPARTMENT_CALLS.map((call) => {
                  const Icon = call.icon || PhoneCall;
                  return (
                    <Button
                      key={call.key}
                      type="button"
                      onClick={() => startLiveCall(call)}
                      className={`flex min-h-[60px] w-full items-center justify-start gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all hover:bg-white active:scale-[0.98] ${
                        call.urgent ? "border-red-200 bg-red-50/90" : "border-slate-200/90 bg-white/95"
                      }`}
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                        call.urgent ? "bg-red-100 text-red-700" : "bg-[#f1e1df] text-[#923536]"
                      }`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <span className="block text-xs font-bold text-slate-900 sm:text-sm">{call.label}</span>
                        <span className="block text-[11px] leading-tight text-slate-500">
                          {call.shortLabel || call.department}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {lastDepartment && (
        <div className="mt-3.5 rounded-2xl border border-slate-200 bg-white/90 p-3 text-xs leading-relaxed text-slate-700 backdrop-blur-sm">
          Calling <span className="font-bold text-slate-900">{lastDepartment}</span>. If staff are busy, you can also leave a request from Hotel Services.
        </div>
      )}
    </section>
  );
};

export default GuestDepartmentCallPanel;
